import { FormControl, Grid, IconButton, InputAdornment, InputLabel, Select, TextField, Box, Typography, Tooltip } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import {
    Search as IconSearch,
    AddCircle as IconAddCircle,
    Edit as IconEdit,
    Clear as IconClear
} from "@mui/icons-material";
import { FormHelperText as HelperText } from '@mui/material';

import { init, setEntity, setData, setListCargo, setValidationMessage } from "../redux/trabajadorSlice";
import * as catalogoService from "../services/catalogoService";
import * as cargoService from "../services/cargoService";
import * as filialService from "../services/filialService";
import trabajadorService from "../services/trabajadorService";
import PersonSearch from './PersonSearch';
import PersonRegister from './person/PersonRegister';

// Catalogo de tipos de cargo del personal: terapeuta, secretaria, personal de
// limpieza, director, coordinador, etc.
const CATALOGO_TIPOS_CARGO = 236;

// Formulario del personal. La persona es una persona natural ya registrada o
// que puede registrarse directamente desde el formulario.
const Trabajador = (props) => {
    const isReadOnly = props.isReadOnly;
    const sliceKey = props.sliceKey;
    const params = useParams();
    const dispatch = useDispatch();

    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const entity = useSelector((state) => state.trabajador[sliceKey]?.entity) || {};
    const listCargo = useSelector((state) => state.trabajador[sliceKey]?.listCargo) || [];
    const validate = useSelector((state) => state.trabajador[sliceKey]?.validationActive) || {};
    const [listFilial, setListFilial] = React.useState([]);

    const [openSearchPersona, setOpenSearchPersona] = React.useState(false);
    const openCloseSearchPersona = () => { setOpenSearchPersona(!openSearchPersona) };

    const [openPersonRegister, setOpenPersonRegister] = React.useState(false);
    const openClosePersonRegister = () => { setOpenPersonRegister(!openPersonRegister) };

    const filialEfectiva = entity?.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 0);

    const validation = {
        idPersona: {
            error: !entity.idPersona || entity.idPersona == 0,
            message: "Seleccione a la persona del personal",
        },
        idFilial: {
            error: !filialEfectiva || filialEfectiva === 0,
            message: "Seleccione la filial",
        },
        idCargo: {
            error: !entity.idCargo || entity.idCargo == 0,
            message: "Seleccione el cargo",
        },
    }

    const messagesValidation = () => {
        var messages = [];

        Object.keys(validation).forEach(function (key) {
            if (validation[key].error) messages.push(validation[key].message)
        })

        return messages;
    }

    useEffect(() => {
        dispatch(init({ sliceKey: sliceKey }));
    }, [dispatch, sliceKey])

    const fetchEntity = useCallback(async () => {
        if (!params.id || params.id == 0) return;

        const response = await trabajadorService.getTrabajadorView(params.id)
            .catch((err) => { console.log("Err: ", err) });

        // La vista devuelve el id del trabajador en "id" y el nombre en "nombres".
        if (response) {
            dispatch(setEntity({ sliceKey: sliceKey, data: {
                ...response,
                idTrabajador: response.id,
                persona: response.nombres || "",
                fechaIngreso: response.fechaIngreso ? moment(response.fechaIngreso).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
            } }));
        }
    }, [params.id, sliceKey, dispatch])

    const fetchListFilial = useCallback(async () => {
        const data = await filialService.getFilialesList().catch(() => []);
        const lista = filialService.soloFilialDelUsuario(data, idFilialUsuario);
        setListFilial(lista);
        if (idFilialUsuario > 0 && (!params.id || params.id == 0)) {
            dispatch(setData({ sliceKey: sliceKey, name: "idFilial", value: idFilialUsuario }));
        }
    }, [idFilialUsuario, params.id, sliceKey, dispatch]);

    useEffect(() => {
        fetchListFilial();
    }, [fetchListFilial]);

    const fetchListCargo = useCallback(async () => {
        try {
            const data = await cargoService.getsCargoViewSearchBasic("", 2);
            if (data && data.length > 0) {
                dispatch(setListCargo({ sliceKey: sliceKey, data: data }));
            } else {
                const catData = await catalogoService.getCatalogosByPadre(CATALOGO_TIPOS_CARGO);
                dispatch(setListCargo({ sliceKey: sliceKey, data: catData || [] }));
            }
        } catch (err) {
            console.log("Err: ", err);
        }
    }, [])

    useEffect(() => {
        fetchEntity();
        fetchListCargo();
        if (!params.id || params.id == 0) {
            dispatch(setData({ sliceKey: sliceKey, name: "fechaIngreso", value: moment().format('YYYY-MM-DD') }));
        }
    }, [fetchEntity, fetchListCargo, params.id, sliceKey, dispatch])

    useEffect(() => {
        dispatch(setValidationMessage({ sliceKey: sliceKey, message: messagesValidation() }));
    }, [entity, idFilialUsuario])

    const setSelectSearchPersona = (select) => {
        if (select) {
            dispatch(setData({ sliceKey: sliceKey, name: "idPersona", value: select.idPersona || select.id }));
            dispatch(setData({ sliceKey: sliceKey, name: "persona", value: select.nombres }));
            dispatch(setData({ sliceKey: sliceKey, name: "tipoDocumento", value: select.tipoDocumento || "" }));
            dispatch(setData({ sliceKey: sliceKey, name: "numeroDocumento", value: select.numeroDocumento || "" }));
        }
    }

    const handlePersonSaved = (result) => {
        if (result) {
            dispatch(setData({ sliceKey: sliceKey, name: "idPersona", value: result.id || result.idPersona }));
            dispatch(setData({ sliceKey: sliceKey, name: "persona", value: result.nombres }));
            dispatch(setData({ sliceKey: sliceKey, name: "tipoDocumento", value: result.tipoDocumento || "" }));
            dispatch(setData({ sliceKey: sliceKey, name: "numeroDocumento", value: result.numeroDocumento || "" }));
        }
    }

    return (
        <Grid container>
            <Grid item>
                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                    <Typography variant="h7">
                        Datos Básicos
                    </Typography>
                </Box>
            </Grid>
            <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                <Grid item xs={12} sm={4.5}>
                    <TextField
                        required
                        name="persona"
                        label="Persona"
                        fullWidth
                        value={entity?.persona || ""}
                        error={validate.idPersona && validation.idPersona.error}
                        helperText={validate.idPersona && validation.idPersona.error ? validation.idPersona.message : ""}
                        size="small"
                        placeholder="Buscar o registrar persona..."
                        InputLabelProps={{ shrink: true }}
                        disabled={isReadOnly}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    {entity?.idPersona > 0 && !isReadOnly && (
                                        <Tooltip title="Quitar selección" arrow>
                                            <span>
                                                <IconButton
                                                    aria-label="quitar persona"
                                                    size="small"
                                                    onClick={() => {
                                                        dispatch(setData({ sliceKey: sliceKey, name: "idPersona", value: 0 }));
                                                        dispatch(setData({ sliceKey: sliceKey, name: "persona", value: "" }));
                                                        dispatch(setData({ sliceKey: sliceKey, name: "tipoDocumento", value: "" }));
                                                        dispatch(setData({ sliceKey: sliceKey, name: "numeroDocumento", value: "" }));
                                                    }}
                                                >
                                                    <IconClear fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={(entity?.idPersona > 0 ? "Editar" : "Registrar") + " Persona"} arrow>
                                        <span>
                                            <IconButton
                                                aria-label="registrar persona"
                                                size="small"
                                                edge="end"
                                                disabled={isReadOnly}
                                                onClick={openClosePersonRegister}
                                            >
                                                {entity?.idPersona > 0 ? <IconEdit fontSize="small" /> : <IconAddCircle fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={"Buscar Persona"} arrow>
                                        <span>
                                            <IconButton
                                                aria-label="Buscar Persona"
                                                size="small"
                                                edge="end"
                                                disabled={isReadOnly}
                                                onClick={openCloseSearchPersona}
                                            >
                                                <IconSearch fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormControl required fullWidth error={validate.idFilial && validation.idFilial.error}>
                        <InputLabel htmlFor="input-filial">Filial</InputLabel>
                        <Select
                            native
                            label="Filial"
                            value={entity?.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 0)}
                            onChange={(event) => dispatch(setData({ sliceKey: sliceKey, name: "idFilial", value: parseInt(event.target.value) || 0 }))}
                            inputProps={{ name: 'idFilial', id: 'input-filial' }}
                            disabled={isReadOnly || idFilialUsuario > 0}
                            size="small"
                        >
                            {idFilialUsuario <= 0 && <option key={0} value={0}>{"(Seleccione)"}</option>}
                            {listFilial.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion || row.nombre}</option>
                            ))}
                        </Select>
                        <HelperText>
                            {validate.idFilial && validation.idFilial.error ? validation.idFilial.message : ""}
                        </HelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={2.5}>
                    <FormControl required fullWidth error={validate.idCargo && validation.idCargo.error}>
                        <InputLabel htmlFor="input-cargo">Cargo</InputLabel>
                        <Select
                            native
                            label="Cargo"
                            value={entity?.idCargo || 0}
                            onChange={(event) => dispatch(setData({ sliceKey: sliceKey, name: "idCargo", value: event.target.value }))}
                            inputProps={{ name: 'idCargo', id: 'input-cargo' }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={0} value={0}>{"(Seleccione)"}</option>
                            {listCargo.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion}</option>
                            ))}
                        </Select>
                        <HelperText>
                            {validate.idCargo && validation.idCargo.error ? validation.idCargo.message : ""}
                        </HelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={2}>
                    <FormControl fullWidth>
                        <InputLabel htmlFor="input-estado">Estado</InputLabel>
                        <Select
                            native
                            label="Estado"
                            value={entity?.idEstado || 0}
                            onChange={(event) => dispatch(setData({ sliceKey: sliceKey, name: "idEstado", value: event.target.value }))}
                            inputProps={{ name: 'idEstado', id: 'input-estado' }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={2} value={2}>{"Activo"}</option>
                            <option key={3} value={3}>{"Inactivo"}</option>
                            <option key={4} value={4}>{"Anulado"}</option>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        name="fechaIngreso"
                        label="Fecha Ingreso"
                        type="date"
                        fullWidth
                        value={moment(entity?.fechaIngreso || moment()).format('YYYY-MM-DD')}
                        onChange={(event) => dispatch(setData({ sliceKey: sliceKey, name: "fechaIngreso", value: event.target.value }))}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        name="seudonimo"
                        label="Seudónimo"
                        fullWidth
                        value={entity?.seudonimo || ""}
                        onChange={(event) => dispatch(setData({ sliceKey: sliceKey, name: "seudonimo", value: event.target.value }))}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>
            <PersonSearch empresa="N" open={openSearchPersona} openClose={openCloseSearchPersona} setSelection={setSelectSearchPersona} />
            <PersonRegister
                id={entity?.idPersona || 0}
                sliceKey="trabajadorPersonRegister"
                open={openPersonRegister}
                openClose={openClosePersonRegister}
                setNewPersonSaved={handlePersonSaved}
            />
        </Grid>
    )
}

export default Trabajador;
